import React, { useState } from 'react';
import { Download } from 'lucide-react';
import SectionEyebrow from '../components/SectionEyebrow';
import OpenTableWidget from '../components/OpenTableWidget';
import MenuModal from '../components/MenuModal';
import { usePage, img } from '../lib/content';
import SmartMedia from '../components/SmartMedia';
import { resolveMediaSrc } from '../lib/api';
import Seo from '../components/Seo';

const Tavern = () => {
  const c = usePage('tavern');
  const specials = Array.isArray(c.specials) ? c.specials : [];
  const [menuOpen, setMenuOpen] = useState(false);

  const downloadSpecial = (s) => {
    if (!s.fileUrl) return;
    const src = resolveMediaSrc(s.fileUrl);
    const url = src.includes('/api/media/') ? `${src}${src.includes('?') ? '&' : '?'}download=1` : src;
    const a = document.createElement('a');
    a.href = url;
    a.download = s.title || 'menu';
    a.target = '_blank';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="bg-[#f7f1e3]">
      <Seo title="The Tavern at Millpond Gatherings · Craft Cocktails & Live Music · Northford, CT" description="A cozy 1700s tavern in Northford, CT serving craft cocktails, comfort food, weekly specials, and live music with a seasonal river patio. Serving North Haven, Wallingford, Hamden & New Haven County." path="/tavern" breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'The Tavern', path: '/tavern' }]} />
      {menuOpen && <MenuModal open onClose={() => setMenuOpen(false)} title="The Tavern · Menu" menus={c.menus} />}
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[640px] w-full overflow-hidden">
        <SmartMedia src={c.heroImage} alt="Tavern" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#f7f1e3] via-transparent to-transparent" style={{ backgroundSize: '100% 30%', backgroundRepeat: 'no-repeat', backgroundPosition: 'bottom' }} />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-32">
          <SectionEyebrow light>{c.heroEyebrow}</SectionEyebrow>
          <h1 className="font-serif text-[#f7f1e3] text-[64px] md:text-[96px] lg:text-[140px] tracking-[-0.02em] mt-4 leading-none">
            {c.heroTitle}
          </h1>
        </div>
      </section>

      {/* VIBE */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-6">
            <SectionEyebrow>{c.vibeEyebrow}</SectionEyebrow>
            <h2 className="font-serif text-[#942222] text-[40px] md:text-[56px] lg:text-[64px] leading-[1.05] tracking-[-0.02em] mt-5">
              {c.vibeTitle}
            </h2>
          </div>
          <div className="lg:col-span-6 space-y-5 text-[#942222]/85 text-[16px] leading-relaxed">
            <p>{c.vibeBody1}</p>
            <p>{c.vibeBody2}</p>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              data-testid="tavern-view-menu"
              className="mt-4 inline-flex items-center gap-2 text-[#942222] font-medium text-[13px] tracking-[0.18em] uppercase border-b border-[#942222] pb-1 hover:gap-3 transition-all"
            >
              {c.menuLabel}
            </button>
          </div>
        </div>
      </section>

      {/* SPECIALS */}
      <section className="py-24 lg:py-32 bg-[#942222] text-[#f1ead9]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionEyebrow light>{c.specialsEyebrow}</SectionEyebrow>
          <h2 className="font-serif text-[44px] md:text-[60px] lg:text-[72px] leading-[1.05] tracking-[-0.02em] mt-5 max-w-3xl">
            {c.specialsTitle}
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {specials.map((s, i) => (
              <div key={i} className="border border-[#f1ead9]/20 p-8 lg:p-10 hover:bg-[#b13030]/40 transition-colors">
                {s.image && (
                  <div className="-mx-8 -mt-8 lg:-mx-10 lg:-mt-10 mb-6 aspect-[16/9] overflow-hidden">
                    <SmartMedia src={s.image} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="text-[11px] tracking-[0.28em] uppercase text-[#e2b95a]">{s.day}</p>
                <h3 className="font-serif text-3xl md:text-4xl mt-3 tracking-tight">{s.title}</h3>
                <p className="text-[#f1ead9]/80 mt-3 text-[15px] leading-relaxed">{s.desc}</p>
                {s.fileUrl && (
                  <button
                    type="button"
                    onClick={() => downloadSpecial(s)}
                    data-testid={`tavern-special-menu-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#e2b95a] border-b border-[#e2b95a] pb-1 hover:gap-3 transition-all"
                  >
                    View Menu <Download size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RIVER PATIO */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="order-2 lg:order-1 aspect-[4/3] overflow-hidden">
            <SmartMedia src={c.patioImage} alt="River patio" className="w-full h-full object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <SectionEyebrow>{c.patioEyebrow}</SectionEyebrow>
            <h2 className="font-serif text-[#942222] text-[40px] md:text-[56px] lg:text-[64px] leading-[1.05] tracking-[-0.02em] mt-5">
              {c.patioTitle}
            </h2>
            <p className="mt-6 text-[#942222]/80 text-[17px] leading-relaxed max-w-lg">
              {c.patioBody}
            </p>
          </div>
        </div>
      </section>

      {/* OPENTABLE INLINE BANNER */}
      <section className="py-20 lg:py-24 bg-[#efe6d0]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
          <SectionEyebrow>{c.bookEyebrow}</SectionEyebrow>
          <h2 className="font-serif text-[#942222] text-[36px] md:text-[48px] lg:text-[56px] leading-[1.05] tracking-[-0.02em] mt-5">
            {c.bookTitle}
          </h2>
          <p className="mt-5 text-[#942222]/75 text-[15px] max-w-md mx-auto">
            {c.bookBody}
          </p>
          <div className="mt-10 flex justify-center">
            <OpenTableWidget rid={c.opentableRid} theme="standard" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tavern;
